import type { UnknownAction } from '@reduxjs/toolkit';
import type {
  DismissFaqSearchMissResult,
  FaqAdminTree,
  FaqCategory,
  FaqQuestion,
  FaqSearchMissList,
} from '@shared/schemas';
import type { Channel } from 'redux-saga';
import {
  actionChannel,
  all,
  call,
  getContext,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';
import { logger } from '@/utils/logger';

import type { FaqGateway } from '../../_shared/infrastructure/faq.gateway';
import {
  createCategoryRequested,
  createQuestionRequested,
  deleteCategoryRequested,
  deleteQuestionRequested,
  loadRequested,
  moveCategoryRequested,
  moveQuestionRequested,
  restoreCategoryRequested,
  restoreQuestionRequested,
  searchMissDismissRequested,
  searchMissRestoreRequested,
  updateCategoryRequested,
  updateQuestionRequested,
} from './admin-faq.actions';
import { selectAdminFaqCategories } from './admin-faq.selectors';
import { adminFaqActions } from './admin-faq.slice';

type Categories = ReturnType<typeof selectAdminFaqCategories>;

/** The backend's own message when it has one ("Move or delete its 2 questions first."). */
function toMessage(e: unknown, fallback: string): string {
  return classifyInfraError(e, fallback).message;
}

/** WORKER: initial load (drives the first-mount spinner). */
function* loadWorker() {
  const gateway: FaqGateway = yield getContext('faqGateway');
  yield put(adminFaqActions.loadStarted());
  try {
    const tree: FaqAdminTree = yield call(gateway.loadAdminFaq);
    yield put(adminFaqActions.loadSucceeded(tree.categories));
  } catch {
    yield put(adminFaqActions.loadFailed());
  }
}

/**
 * WORKER: the searches with no answer (#735), alongside the tree. A failure
 * only hides their card: the FAQ itself is what the page is for.
 */
function* loadSearchMissesWorker() {
  const gateway: FaqGateway = yield getContext('faqGateway');
  try {
    const list: FaqSearchMissList = yield call(gateway.loadSearchMisses);
    yield put(adminFaqActions.searchMissesLoaded(list.items));
  } catch (e) {
    logger.warn('Could not load the FAQ searches with no answer', e);
    yield put(adminFaqActions.searchMissesFailed());
  }
}

/**
 * WORKER: dismiss a search, off the list at once. Taken: the page offers Undo
 * with the server's stamp. Refused: the search comes back, and the page says so.
 */
function* dismissSearchMissWorker(action: ReturnType<typeof searchMissDismissRequested>) {
  const gateway: FaqGateway = yield getContext('faqGateway');
  const { item, index } = action.payload;
  yield put(adminFaqActions.searchMissHidden(item.normalized_query));
  try {
    const result: DismissFaqSearchMissResult = yield call(
      gateway.dismissSearchMiss,
      item.normalized_query,
    );
    yield put(
      adminFaqActions.searchMissNoticed({
        kind: 'dismissed',
        item,
        index,
        dismissedAt: result.dismissed_at,
      }),
    );
  } catch (e) {
    yield put(adminFaqActions.searchMissShownAgain({ item, index }));
    yield put(
      adminFaqActions.searchMissNoticed({
        kind: 'failed',
        message: toMessage(e, 'Could not dismiss the search.'),
      }),
    );
  }
}

/** WORKER: the Undo of a dismiss, back on the list at once. Refused: off again. */
function* restoreSearchMissWorker(action: ReturnType<typeof searchMissRestoreRequested>) {
  const gateway: FaqGateway = yield getContext('faqGateway');
  const { item, index, dismissedAt } = action.payload;
  yield put(adminFaqActions.searchMissShownAgain({ item, index }));
  try {
    yield call(gateway.restoreSearchMiss, item.normalized_query, dismissedAt);
  } catch (e) {
    yield put(adminFaqActions.searchMissHidden(item.normalized_query));
    yield put(
      adminFaqActions.searchMissNoticed({
        kind: 'failed',
        message: toMessage(e, 'Could not bring the search back.'),
      }),
    );
  }
}

/**
 * Put the server's tree back after a refused move: the optimistic order on screen
 * is wrong. Silent on failure: the refusal is already shown, and the tree on
 * screen is still usable.
 */
function* reloadAfterRefusal(gateway: FaqGateway) {
  try {
    const tree: FaqAdminTree = yield call(gateway.loadAdminFaq);
    yield put(adminFaqActions.loadSucceeded(tree.categories));
  } catch {
    // Keep what is on screen.
  }
}

function* createCategory(gateway: FaqGateway, action: ReturnType<typeof createCategoryRequested>) {
  try {
    const category: FaqCategory = yield call(gateway.createCategory, action.payload);
    yield put(adminFaqActions.categoryCreated(category));
  } catch (e) {
    return toMessage(e, 'Could not create the category.');
  }
  return null;
}

function* updateCategory(gateway: FaqGateway, action: ReturnType<typeof updateCategoryRequested>) {
  try {
    const category: FaqCategory = yield call(
      gateway.updateCategory,
      action.payload.id,
      action.payload.changes,
    );
    yield put(adminFaqActions.categoryUpdated(category));
  } catch (e) {
    return toMessage(e, 'Could not update the category.');
  }
  return null;
}

/** Soft delete; the backend refuses (409) while the category still holds questions. */
function* deleteCategory(gateway: FaqGateway, action: ReturnType<typeof deleteCategoryRequested>) {
  const categories: Categories = yield select(selectAdminFaqCategories);
  const label = categories.find((c) => c.id === action.payload)?.name ?? '';
  try {
    yield call(gateway.deleteCategory, action.payload);
    yield put(adminFaqActions.categoryDeleted({ kind: 'category', id: action.payload, label }));
  } catch (e) {
    return toMessage(e, 'Could not delete the category.');
  }
  return null;
}

/**
 * Undo. The server puts the item back at its old index; reloading the tree is
 * the simplest way to show exactly that.
 */
function* restore(gateway: FaqGateway, restoreItem: () => Promise<unknown>, fallback: string) {
  try {
    yield call(restoreItem);
    const tree: FaqAdminTree = yield call(gateway.loadAdminFaq);
    yield put(adminFaqActions.restored(tree.categories));
  } catch (e) {
    yield put(adminFaqActions.lastDeletedCleared());
    return toMessage(e, fallback);
  }
  return null;
}

/** Optimistic reorder, then the full id list; reload when the server refuses it. */
function* moveCategory(gateway: FaqGateway, action: ReturnType<typeof moveCategoryRequested>) {
  yield put(adminFaqActions.categoryMoved(action.payload));
  const categories: Categories = yield select(selectAdminFaqCategories);
  try {
    yield call(
      gateway.reorderCategories,
      categories.map((c) => c.id),
    );
  } catch (e) {
    yield call(reloadAfterRefusal, gateway);
    return toMessage(e, 'Could not move the category.');
  }
  return null;
}

function* createQuestion(gateway: FaqGateway, action: ReturnType<typeof createQuestionRequested>) {
  try {
    const question: FaqQuestion = yield call(gateway.createQuestion, action.payload);
    yield put(adminFaqActions.questionCreated(question));
  } catch (e) {
    return toMessage(e, 'Could not create the question.');
  }
  return null;
}

function* updateQuestion(gateway: FaqGateway, action: ReturnType<typeof updateQuestionRequested>) {
  try {
    const question: FaqQuestion = yield call(
      gateway.updateQuestion,
      action.payload.id,
      action.payload.changes,
    );
    yield put(adminFaqActions.questionUpdated(question));
  } catch (e) {
    return toMessage(e, 'Could not update the question.');
  }
  return null;
}

function* deleteQuestion(gateway: FaqGateway, action: ReturnType<typeof deleteQuestionRequested>) {
  const categories: Categories = yield select(selectAdminFaqCategories);
  const label =
    categories.flatMap((c) => c.questions).find((q) => q.id === action.payload)?.question ?? '';
  try {
    yield call(gateway.deleteQuestion, action.payload);
    yield put(adminFaqActions.questionDeleted({ kind: 'question', id: action.payload, label }));
  } catch (e) {
    return toMessage(e, 'Could not delete the question.');
  }
  return null;
}

/** Optimistic move (same or another category); reload when the server refuses it. */
function* moveQuestion(gateway: FaqGateway, action: ReturnType<typeof moveQuestionRequested>) {
  const { id, categoryId, index } = action.payload;
  yield put(adminFaqActions.questionMoved(action.payload));
  try {
    yield call(gateway.moveQuestion, id, {
      category_id: categoryId,
      index: Math.max(0, Math.trunc(index)),
    });
  } catch (e) {
    yield call(reloadAfterRefusal, gateway);
    return toMessage(e, 'Could not move the question.');
  }
  return null;
}

/** The request id a write signal carries (see `withRequestId`). */
function requestIdOf(action: UnknownAction): string {
  const meta: unknown = 'meta' in action ? action.meta : undefined;
  if (
    meta &&
    typeof meta === 'object' &&
    'requestId' in meta &&
    typeof meta.requestId === 'string'
  ) {
    return meta.requestId;
  }
  return 'unknown';
}

/**
 * Route one write signal to its worker, then record how it ended under the
 * signal's request id. A worker returns the message to show, or null.
 */
function* mutationWorker(gateway: FaqGateway, action: UnknownAction) {
  const requestId = requestIdOf(action);
  yield put(adminFaqActions.mutationStarted(requestId));
  let error: string | null = null;
  if (createCategoryRequested.match(action)) error = yield call(createCategory, gateway, action);
  else if (updateCategoryRequested.match(action))
    error = yield call(updateCategory, gateway, action);
  else if (deleteCategoryRequested.match(action))
    error = yield call(deleteCategory, gateway, action);
  else if (restoreCategoryRequested.match(action))
    error = yield call(
      restore,
      gateway,
      () => gateway.restoreCategory(action.payload),
      'Could not restore the category.',
    );
  else if (moveCategoryRequested.match(action)) error = yield call(moveCategory, gateway, action);
  else if (createQuestionRequested.match(action))
    error = yield call(createQuestion, gateway, action);
  else if (updateQuestionRequested.match(action))
    error = yield call(updateQuestion, gateway, action);
  else if (deleteQuestionRequested.match(action))
    error = yield call(deleteQuestion, gateway, action);
  else if (restoreQuestionRequested.match(action))
    error = yield call(
      restore,
      gateway,
      () => gateway.restoreQuestion(action.payload),
      'Could not restore the question.',
    );
  else if (moveQuestionRequested.match(action)) error = yield call(moveQuestion, gateway, action);
  yield put(
    error === null
      ? adminFaqActions.mutationSucceeded(requestId)
      : adminFaqActions.mutationFailed({ requestId, error }),
  );
}

const MUTATION_TYPES = [
  createCategoryRequested.type,
  updateCategoryRequested.type,
  deleteCategoryRequested.type,
  restoreCategoryRequested.type,
  moveCategoryRequested.type,
  createQuestionRequested.type,
  updateQuestionRequested.type,
  deleteQuestionRequested.type,
  restoreQuestionRequested.type,
  moveQuestionRequested.type,
];

/**
 * WATCHER for every write, one at a time, in dispatch order.
 *
 * Not `takeEvery`: two quick "Move down" clicks would race, and the server could
 * apply the second move before the first. Queued, each move is computed and sent
 * against the order the previous one left, and the backend's positions match
 * what the screen shows. Not `takeLatest` either: a write must never be dropped.
 */
function* mutationQueue() {
  const gateway: FaqGateway = yield getContext('faqGateway');
  const channel: Channel<UnknownAction> = yield actionChannel(MUTATION_TYPES);
  while (true) {
    const action: UnknownAction = yield take(channel);
    yield call(mutationWorker, gateway, action);
  }
}

/** WATCHERS. */
export function* adminFaqSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(loadRequested.type, loadSearchMissesWorker),
    takeEvery(searchMissDismissRequested.type, dismissSearchMissWorker),
    takeEvery(searchMissRestoreRequested.type, restoreSearchMissWorker),
    mutationQueue(),
  ]);
}
